// ============================================================
// CLUBHUB – Mahnwesen: Stufen, Fristen, Gebühren
// ============================================================
// Gerechnet wird mit dem Mahnstand aus dem Agent
// (`lib/dunning/state.ts`) und der Staffel des Vereins
// (`lib/dunning/regeln.ts`, aus den Einstellungen Finanzen ›
// Rechnungen › Mahnwesen). Diese Datei übersetzt ihn in die Sprache
// der Masken (`MahnStand`: Lage, Stufe, nächste Stufe, Datum, Satz)
// — Liste, Blatt, Mahnlauf und Server fragen damit DIESELBE Regel.
//
// GERECHNET WIRD AUF EIN „HEUTE“: Liste, Blatt und Mahnlauf
// bekommen denselben Stichtag, sonst passt die Summe nicht zu den
// Zeilen darüber.
// ============================================================
import { dunningState } from "@/lib/dunning/state";
import { vereinsMahnregeln, type DunningRules } from "@/lib/dunning/regeln";
import { plusTage } from "./format";
import type { MahnStand, RechnungView } from "./views";
import { formatDate } from "@/lib/utils";

export type Mahnstufe = {
  stufe: number;
  titel: string;
  kurz: string;
  /** Tage nach Fälligkeit (Stufe 1) bzw. nach der vorigen Stufe. */
  abstand: number;
  /** Neue Zahlungsfrist, die das Schreiben setzt. */
  frist: number;
  /** Euro. */
  gebuehr: number;
  /** Zusatz der Stufe (bei der letzten die Folgen nach der Satzung). */
  zusatz: string;
};

export type Mahnregeln = { karenz: number; stufen: Mahnstufe[]; agent: DunningRules };

/** Die Staffel in der Form der Masken. */
export function mahnregelnAus(rules: DunningRules): Mahnregeln {
  return {
    karenz: rules.graceDays,
    agent: rules,
    stufen: rules.levels.map((l, i) => ({
      stufe: l.level,
      titel: l.title,
      kurz: l.short,
      abstand: i === 0 ? rules.graceDays : (rules.levels[i - 1]?.days ?? l.days),
      frist: l.days,
      gebuehr: l.feeCents / 100,
      zusatz: l.notice,
    })),
  };
}

/** Die Vorgaben der Einstellungen — solange ein Verein nichts anderes eingetragen hat. */
export const MAHNREGELN: Mahnregeln = mahnregelnAus(vereinsMahnregeln({}));

export function mahnstufe(stufe: number, regeln: Mahnregeln = MAHNREGELN): Mahnstufe | undefined {
  return regeln.stufen.find((s) => s.stufe === stufe);
}

export function letzteStufe(regeln: Mahnregeln = MAHNREGELN): number {
  return regeln.stufen.length;
}

export const LETZTE_STUFE = MAHNREGELN.stufen.length;

const OFFEN = new Set(["OPEN", "PARTIALLY_PAID", "OVERDUE"]);

/** Ist die Rechnung überhaupt im Mahnwesen? */
export function mahnbar(r: Pick<RechnungView, "status" | "kind" | "openAmount">): boolean {
  return OFFEN.has(r.status) && r.kind !== "CREDIT_NOTE" && r.kind !== "CANCELLATION" && r.openAmount > 0;
}

const tag = (iso: string) => new Date(`${iso.slice(0, 10)}T00:00:00Z`);
const iso = (d: Date | null) => (d ? d.toISOString().slice(0, 10) : null);

/**
 * Wo die Rechnung steht — oder `null`, wenn sie nicht überfällig ist.
 * Eine Stufe ist FÄLLIG, sobald die Frist der vorigen vorbei ist
 * (bzw. die Karenz nach der Fälligkeit); nach der letzten Stufe
 * entscheidet der Vorstand.
 */
export function mahnStand(
  r: Pick<RechnungView, "status" | "kind" | "openAmount" | "dueDate" | "dunningLevel" | "lastDunningAt" | "dunningFees"> &
    Partial<Pick<RechnungView, "lastDunningDue" | "dunningPausedUntil" | "dunningPauseReason">>,
  heute: string,
  regeln: Mahnregeln = MAHNREGELN,
): MahnStand | null {
  if (!mahnbar(r) || r.dueDate >= heute) return null;
  const s = dunningState(
    {
      status: r.status,
      dueDate: tag(r.dueDate),
      totalCents: Math.round(r.openAmount * 100),
      paidCents: 0,
      dunningPausedUntil: r.dunningPausedUntil ? tag(r.dunningPausedUntil) : null,
      dunnings:
        r.dunningLevel > 0 && r.lastDunningAt
          ? [
              {
                level: r.dunningLevel,
                sentAt: tag(r.lastDunningAt),
                dueDate: r.lastDunningDue ? tag(r.lastDunningDue) : null,
                feeCents: Math.round(r.dunningFees * 100),
                emailedAt: null,
              },
            ]
          : [],
    },
    regeln.agent,
    tag(heute),
  );
  const verzugTage = s.overdueDays;
  const erreicht = mahnstufe(s.level, regeln);
  const letzteFrist = s.last ? (iso(s.last.dueDate) ?? plusTage(iso(s.last.sentAt)!, erreicht?.frist ?? 14)) : null;

  switch (s.stage) {
    case "none":
      return null;
    case "paused":
      return {
        lage: "gestundet",
        stufe: s.level,
        naechste: null,
        datum: iso(s.pausedUntil),
        verzugTage,
        satz: `Mahnsperre bis ${formatDate(iso(s.pausedUntil))}${r.dunningPauseReason ? ` — ${r.dunningPauseReason}` : ""}`,
      };
    case "grace":
      return { lage: "karenz", stufe: 0, naechste: null, datum: iso(s.nextDue), verzugTage, satz: `Überfällig, ${mahnstufe(1, regeln)?.titel ?? "Zahlungserinnerung"} ab ${formatDate(iso(s.nextDue))}` };
    case "running":
      return { lage: "frist", stufe: s.level, naechste: null, datum: letzteFrist, verzugTage, satz: `${erreicht?.titel ?? `Stufe ${s.level}`} verschickt, Frist bis ${formatDate(letzteFrist)}` };
    case "exhausted":
      return { lage: "ausgeschoepft", stufe: s.level, naechste: null, datum: letzteFrist, verzugTage, satz: "Alle Stufen verschickt — der Vorstand entscheidet über das Weitere" };
    case "due":
      return {
        lage: "faellig",
        stufe: s.level,
        naechste: s.next!.level,
        datum: iso(s.nextDue),
        verzugTage,
        satz: `${s.next!.title} fällig seit ${formatDate(iso(s.nextDue))}`,
      };
  }
}

/** Kurzform für die Marke in der Liste: „Überfällig (1. M)“. */
export function mahnKuerzel(stufe: number, regeln: Mahnregeln = MAHNREGELN): string | null {
  return mahnstufe(stufe, regeln)?.kurz ?? null;
}
